import React from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";

import { useServices, useSnackbar } from "contexts";
import { getInterviewAttempts } from "services";
import { Session, getAverageScore, round } from "utils";

import LineGraph, { LineGraphSkeleton } from "../LineGraph";
import NullState from "../NullState";

// Same option sets as the desktop PerformanceGraph header.
const ASSESSMENT_TYPES = [
  { _id: "allAssessments", name: "All Assessments" },
  { _id: "practice", name: "Practice" },
  { _id: "assignment", name: "Assignment" },
];

const SCORE_TYPES = [
  { _id: "overall", name: "Overall" },
  { _id: "communication", name: "Speech" },
  { _id: "content", name: "Content" },
];

// The 12/24/36 range maps to the desktop `pageSize` (how many recent attempts
// the graph shows).
const RANGE_OPTIONS = [12, 24, 36];

function LabeledSelect({ label, value, options, onChange }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body02-medium"
        color="neutral.clr-600"
        sx={{ mb: 1.5, display: "block" }}
      >
        {label}
      </Typography>
      <Select
        fullWidth
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        IconComponent={KeyboardArrowDownIcon}
        sx={{
          borderRadius: 2,
          bgcolor: "shades.clr-white-900",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "neutral.clr-200" },
          "& .MuiSelect-select": { py: 2 },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt._id} value={opt._id}>
            {opt.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function RangeToggle({ value, onChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid",
        borderColor: "neutral.clr-200",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "shades.clr-white-900",
      }}
    >
      {RANGE_OPTIONS.map((opt) => {
        const selected = value === opt;
        return (
          <Box
            key={opt}
            role="button"
            onClick={() => onChange(opt)}
            sx={{
              flex: 1,
              textAlign: "center",
              py: 2.5,
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: selected ? "primary.clr-300" : "transparent",
            }}
          >
            <Typography
              variant={selected ? "body01-bold" : "body01-medium"}
              color={selected ? "shades.clr-white-900" : "neutral.clr-700"}
            >
              {opt}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * "Recent Attempts Analytics" section (mobile). Shared by the Dashboard and the
 * Performance page.
 *
 * Fetches recent attempts exactly like the desktop OverallPerformance graph:
 * getInterviewAttempts({ batch, user, finishedAt: null, pageSize, order: 'desc',
 * orderBy: 'createdAt', include: 'interviewTemplate' }), then plots the selected
 * score series (overall/speech/content). The Assessment-Type dropdown adds the
 * `type` filter (refetch); the Score-Type dropdown just switches the plotted
 * series.
 *
 * @param {string} batch            selected batch id
 * @param {boolean} [showRangeToggle] show the 12/24/36 range control (Performance).
 * @param {boolean} [batchLoading]  batch still resolving — keep showing skeletons.
 */
export default function RecentAttemptsAnalytics({
  batch,
  showRangeToggle = false,
  batchLoading = false,
}) {
  const service = useServices();
  const snackbar = useSnackbar();

  const [assessmentType, setAssessmentType] = React.useState("allAssessments");
  const [scoreType, setScoreType] = React.useState("overall");
  const [pageSize, setPageSize] = React.useState(12);

  const [attempts, setAttempts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!batch) return;

    (async () => {
      try {
        setLoading(true);

        const filters = {
          batch,
          user: Session.userId,
          finishedAt: null,
          pageSize,
          order: "desc",
          orderBy: "createdAt",
          include: "interviewTemplate",
        };
        if (assessmentType !== "allAssessments") filters.type = assessmentType;

        const { interviewAttempts } = await service.callService(
          getInterviewAttempts,
          filters
        );

        // Desktop unshifts so the newest attempts end up on the right.
        const mapped = [];
        (interviewAttempts || []).forEach((attempt) => {
          const content = attempt.analysis?.content?.ratings?.OVERALL || 0;
          const communication =
            attempt.analysis?.communication?.ratings?.OVERALL || 0;
          mapped.unshift({
            overall: getAverageScore(communication, content),
            communication,
            content,
          });
        });

        setAttempts(mapped);
      } catch (error) {
        console.error(error);
        snackbar.error("Uh Oh! Unable to load analytics");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, pageSize, assessmentType]);

  const labels = React.useMemo(
    () => attempts.map((_, i) => i + 1),
    [attempts]
  );
  const data = React.useMemo(
    () => attempts.map((a) => round(a[scoreType] || 0)),
    [attempts, scoreType]
  );

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Box>
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          Recent Attempts Analytics
        </Typography>
        <Typography
          variant="body02-regular"
          color="neutral.clr-500"
          sx={{ mt: 1, display: "block" }}
        >
          Overview of your performance for recent attempts
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        <LabeledSelect
          label="Assessment Type:"
          value={assessmentType}
          options={ASSESSMENT_TYPES}
          onChange={setAssessmentType}
        />
        <LabeledSelect
          label="Score Type:"
          value={scoreType}
          options={SCORE_TYPES}
          onChange={setScoreType}
        />
      </Box>

      {batchLoading || loading ? (
        <LineGraphSkeleton />
      ) : data.length ? (
        <LineGraph labels={labels} data={data} />
      ) : (
        // Same copy as the desktop graph's NoAssessmentsFound default label.
        <NullState
          icon={BarChartRoundedIcon}
          description={
            <>
              You have not attempted any assessment so far!
              <br />
              once attempted you can see analytics
            </>
          }
        />
      )}

      {showRangeToggle && <RangeToggle value={pageSize} onChange={setPageSize} />}
    </Box>
  );
}
